/**
 * Expanded detail for a `custom_event` feed root — a backoffice-authored custom
 * event rule that fired for its creator. Renders the operator-defined property
 * rows, the inline list of matched bookings (deep-linked), and a "Show more"
 * link into the bookings table filtered by the rule's milestone + group.
 *
 * The card is creator-only (gated server-side by `payload.owner_user_id`); this
 * component only presents the snapshotted payload, mirroring the operational
 * digest layout so grouped custom cards read consistently in the feed.
 */
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

import { SensitiveInlineText } from "@/components/common/Sensitive";
import { FeedBookingDrillPartyLine } from "./FeedBookingDrillParties";
import type { CustomEventBooking, CustomEventPayload } from "@/types/feed";
import { bookingHref, operationalDigestShowMoreHref } from "./feedLinks";

/** Whether a payload is a `custom_event` root. */
export function isCustomEventPayload(
  payload: unknown,
): payload is CustomEventPayload {
  if (!payload || typeof payload !== "object") return false;
  const p = payload as Record<string, unknown>;
  return p.kind === "custom_event" && Array.isArray(p.bookings);
}

function CustomEventBookingRow({ booking }: { booking: CustomEventBooking }) {
  const hasParties = Boolean(
    booking.agent || booking.consignee || booking.carrier,
  );
  return (
    <div className="rounded-lg bg-white/5 px-2.5 py-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <Link
            to={bookingHref(booking.source_id)}
            className="inline-flex max-w-full items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary transition-colors hover:bg-primary/20"
          >
            #<SensitiveInlineText text={booking.source_id} />
            <ArrowUpRight className="size-2.5 shrink-0" />
          </Link>
          {hasParties && (
            <FeedBookingDrillPartyLine
              variant="summary"
              agent={booking.agent ?? undefined}
              consignee={booking.consignee ?? undefined}
              className="mt-1"
            />
          )}
        </div>
        {booking.milestone_date && (
          <span className="shrink-0 rounded-full bg-white/5 px-1.5 py-0.5 text-[9px] font-medium text-muted-foreground">
            {booking.milestone_date}
          </span>
        )}
      </div>
    </div>
  );
}

export function FeedCustomEventDetail({
  payload,
}: {
  payload: CustomEventPayload;
}) {
  const total = payload.booking_count;
  const shown = payload.bookings.length;
  const showMoreHref = operationalDigestShowMoreHref(payload.show_more);
  const properties = (payload.properties ?? []).filter(
    (p) => p.value && p.value.trim().length > 0,
  );

  // Per-booking cards always show their single booking; grouped cards inherit
  // the generic bookings drill-down only when the rule opts in.
  const showDrilldown =
    payload.grouping_mode === "per_booking" ||
    payload.show_booking_drilldown !== false;

  // Windowed triggers (e.g. a weekly Wed→Tue "Send BL") span a date range;
  // single-day triggers collapse to a single date.
  const periodLabel =
    payload.period_start && payload.period_end
      ? payload.period_start === payload.period_end
        ? payload.period_end
        : `${payload.period_start} → ${payload.period_end}`
      : null;

  return (
    <div className="feed-row-detail-body feed-row-detail-body--loose">
      <p className="feed-row-detail-body__heading">
        {total} booking{total === 1 ? "" : "s"} for {payload.group_label}
      </p>

      {periodLabel && (
        <p className="text-[11px] text-muted-foreground px-0.5">
          <span className="font-medium text-foreground/80">
            {payload.milestone?.toUpperCase() || "Window"}:
          </span>{" "}
          {periodLabel}
        </p>
      )}

      {properties.length > 0 && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 px-0.5">
          {properties.map((prop) => (
            <span key={prop.key} className="text-[11px] text-muted-foreground">
              <span className="font-medium text-foreground/80">{prop.label}:</span>{" "}
              {prop.value}
            </span>
          ))}
        </div>
      )}

      {showDrilldown && (
        <>
          <div className="flex flex-col gap-1.5">
            {payload.bookings.map((booking) => (
              <CustomEventBookingRow key={booking.source_id} booking={booking} />
            ))}
          </div>

          <Link
            to={showMoreHref}
            className="inline-flex w-fit items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary transition-colors hover:bg-primary/20"
          >
            {total > shown ? `Show all ${total} bookings` : "View bookings"}
            <ArrowUpRight className="size-2.5 shrink-0" />
          </Link>
        </>
      )}
    </div>
  );
}
